import type { AppThunk } from '@src/redux/appThunk.ts';
import FJRequirementsUtil from '@src/util/FJRequirementsUtil';
import { getLtsUploadSource, LTS_PARSE_FAIL, setLtsUploadSource, LTS_SOURCE } from '@src/util/FJLongToShortEa';
import FJUtil from '@src/util/FJUtil';
import FJMessageStore, { MESSAGE_TYPE } from '@src/store/FJMessageStore';
import FJLocalStore from '@src/store/FJLocalStore';
import FJYoutubeAPI from '@src/core/network/FJYoutubeAPI.ts';
import FJS3Config from '@src/config/FJS3Config';
import { FJGeneratingPageAction } from '@src/redux/action/FJGeneratingPageAction.ts';
import { FJModalsAction } from '@src/redux/action/FJModalsAction.ts';
import { FJStartLandingAction } from '@src/redux/action/FJStartLandingAction.ts';
import FJVideoUtil from '@src/util/FJVideoUtil';
import FJS3NetworkManager from '@src/store/FJS3NetworkManager';
import FJConfig from '@src/config/FJConfig.ts';

let isProgress = false;

const FJYoutubeAction = {
    submitYoutubeLinkAction:
        (youtubeUrl: string): AppThunk =>
        async (dispatch, getState) => {
            if (isProgress) {
                return;
            }

            dispatch(FJStartLandingAction.clearProjectSelectionAction());

            FJRequirementsUtil.eaLog('youtube_link_paste', { url: youtubeUrl });

            const __isCanceled = () => {
                return !isProgress;
            };
            const __endProgress = () => {
                isProgress = false;
            };
            isProgress = true;
            void getState();
            setLtsUploadSource(LTS_SOURCE.YOUTUBE);
            if (!navigator.onLine) {
                FJMessageStore.addMessage(FJLocalStore._('network-error-please-try-again'), 'error');
                FJRequirementsUtil.eaLog('long_to_shorts_vedio_parse_fail', {
                    source: getLtsUploadSource(),
                    fail_reason: LTS_PARSE_FAIL.NETWORK_OFFLINE,
                    url: youtubeUrl,
                });
                __endProgress();
                return;
            }
            if (!FJRequirementsUtil.isLogin() && !FJUtil.isLocalhost()) {
                __endProgress();
                await FJRequirementsUtil.anonymousSignUp();
                return;
            }

            if (!FJUtil.isYoutubeUrl(youtubeUrl) || FJUtil.containsMultipleUrls(youtubeUrl)) {
                __endProgress();
                FJRequirementsUtil.eaLog('long_to_shorts_vedio_parse_fail', {
                    source: getLtsUploadSource(),
                    fail_reason: LTS_PARSE_FAIL.INVALID_YOUTUBE_LINK,
                    url: youtubeUrl,
                });
                FJMessageStore.addMessage(FJLocalStore._('invalid-youtube-link'), 'error');
                return;
            }

            dispatch(
                FJModalsAction.showLoadingModalAction(FJLocalStore._('loading'), () => {
                    __endProgress();
                    FJYoutubeAPI.cancelDownload();
                }),
            );

            const __jumpToNext = (file: File, downloadUrl?: string, isExistS3?: boolean) => {
                dispatch(
                    FJGeneratingPageAction.setVideoMetaAction(
                        file,
                        {
                            sourceUrl: youtubeUrl,
                            youtubeKey: youtubeId,
                            thumbnailUrl: thumbnail_url,
                            fileName: title,
                            resolutionLabel: `${width}x${height}`,
                            downloadUrl: downloadUrl,
                            isExistS3: isExistS3,
                        },
                        () => {
                            return !isProgress;
                        },
                        () => {
                            __endProgress();
                            dispatch(FJModalsAction.hideLoadingModalAction());
                        },
                    ),
                );
            };

            // 获取视频数据，判断是否可用
            const resData = await FJYoutubeAPI.getYoutubeLinkInfoWithServerless(youtubeUrl).catch(() => {
                return null;
            });
            if (__isCanceled()) {
                __endProgress();
                return;
            }
            const { title, duration, width, height, thumbnail_url, downloadScheme } = resData || {};
            const youtubeDownloadScheme: string = !downloadScheme ? FJConfig.YOUTUBE_DOWNLOAD_SCHEME : downloadScheme;
            if (!duration || !width || !height) {
                FJRequirementsUtil.eaLog('long_to_shorts_vedio_parse_fail', {
                    source: getLtsUploadSource(),
                    fail_reason: LTS_PARSE_FAIL.YOUTUBE_META_INVALID,
                    url: youtubeUrl,
                });
                FJMessageStore.addMessage(FJLocalStore._('failed-to-check-youtube-link'), MESSAGE_TYPE.ERROR);
                __endProgress();
                dispatch(FJModalsAction.hideLoadingModalAction());
                return;
            }

            if (
                duration < FJConfig.VIDEO_UPLOAD_MIN_DURATION_SEC ||
                duration > FJConfig.VIDEO_UPLOAD_MAX_DURATION_SEC
            ) {
                FJRequirementsUtil.eaLog('long_to_shorts_vedio_parse_fail', {
                    source: getLtsUploadSource(),
                    fail_reason: LTS_PARSE_FAIL.YOUTUBE_DURATION_INVALID,
                    url: youtubeUrl,
                });
                FJMessageStore.addMessage(
                    FJLocalStore._('please-upload-a-video-between-1-and-120-minutes'),
                    MESSAGE_TYPE.ERROR,
                );
                __endProgress();
                dispatch(FJModalsAction.hideLoadingModalAction());
                return;
            }

            const youtubeId = FJUtil.extractYoutubeVideoId(youtubeUrl || '');
            const videoUploadKey = FJS3Config.INPUT_UPLOAD_KEY + youtubeId + '.mp4';

            const localData = await FJVideoUtil.getFileListFromLocalStorage(
                FJS3Config.S3_SERVER_HOST + videoUploadKey,
            ).catch(() => {});
            if (__isCanceled()) {
                return;
            }
            // 有本地的缓存数据,直接使用本地的数据
            if (localData) {
                const localFile = localData.data;
                const isSupport = await FJVideoUtil.checkVideoFileIsSupport(localFile);
                if (!isSupport) {
                    //文件被损坏
                    FJVideoUtil.removeFileListFromLocalStorage(FJS3Config.S3_SERVER_HOST + videoUploadKey).catch(
                        () => {},
                    );
                    FJMessageStore.addMessage(
                        FJLocalStore._('the-file-could-not-be-downloaded-please-try-again'),
                        MESSAGE_TYPE.ERROR,
                    );
                    __endProgress();
                    dispatch(FJModalsAction.hideLoadingModalAction());

                    return;
                }
                __jumpToNext(localFile);
            } else {
                let isExistS3 = await FJS3NetworkManager.checkS3FileExists(videoUploadKey);
                let downloadUrl: string = '';
                if (isExistS3) {
                    downloadUrl = FJS3Config.S3_SERVER_HOST + videoUploadKey;
                } else {
                    const youTubeUrlResult = await FJYoutubeAPI.downloadYoutubeVideo(
                        youtubeUrl,
                        youtubeDownloadScheme,
                    ).catch(e => {
                        if (!__isCanceled()) {
                            FJMessageStore.addMessage(FJLocalStore._('failed-to-check-youtube-link'), 'error');
                        }
                        dispatch(FJModalsAction.hideLoadingModalAction());
                        // TODO 关闭进度条
                        if (e.message === 'Canceled') {
                            __endProgress();
                            return undefined;
                        }
                    });
                    if (__isCanceled()) {
                        __endProgress();
                        return;
                    }
                    // TODO 服务商方案
                    if (youtubeDownloadScheme === 'SERVICE_PROVIDER') {
                        // const proxyUrl = FJConfig.IS_TEST
                        //     ? 'https://www.smartvideomaker.com/'
                        //     : 'https://www.flexclip.com/';
                        // downloadUrl = `${proxyUrl}proxy?videoURL=${youTubeUrlResult}`;
                        downloadUrl = FJS3Config.S3_SERVER_HOST + FJS3Config.INPUT_UPLOAD_KEY + youTubeUrlResult;
                    } else {
                        // TODO 自己服务器的方案
                        downloadUrl = FJS3Config.S3_SERVER_HOST + 'ai/input/long-video-cut/' + youTubeUrlResult;
                    }
                    isExistS3 = await FJS3NetworkManager.checkS3FileExists(
                        FJS3Config.INPUT_UPLOAD_KEY + youTubeUrlResult,
                    );
                }

                if (!downloadUrl) {
                    FJRequirementsUtil.eaLog('long_to_shorts_vedio_parse_fail', {
                        source: getLtsUploadSource(),
                        fail_reason: LTS_PARSE_FAIL.YOUTUBE_DOWNLOAD_FAIL,
                        url: youtubeUrl,
                    });
                    __endProgress();
                    dispatch(FJModalsAction.hideLoadingModalAction());
                    return;
                }
                try {
                    if (!isExistS3) {
                        const res: any = await fetch(downloadUrl, { method: 'GET' });
                        if (!res.ok) {
                            throw new Error('视频下载失败');
                        }
                        const blob = await res.blob();
                        if (__isCanceled()) {
                            __endProgress();
                            return;
                        }
                        const file = new File([blob], title, { type: 'video/mp4' });
                        // 添加到缓存
                        if (youtubeId) {
                            await FJVideoUtil.saveFileListToLocalStorage([
                                { key: downloadUrl, file, isFile: true },
                            ]).catch(() => {});
                        }
                        __jumpToNext(file);
                    } else {
                        __jumpToNext(undefined as unknown as File, downloadUrl, true);
                    }
                } catch (_e) {
                    if (__isCanceled()) {
                        __endProgress();
                        dispatch(FJModalsAction.hideLoadingModalAction());
                        return;
                    }
                    FJRequirementsUtil.eaLog('long_to_shorts_vedio_parse_fail', {
                        source: getLtsUploadSource(),
                        fail_reason: LTS_PARSE_FAIL.YOUTUBE_DOWNLOAD_FAIL,
                        url: youtubeUrl,
                    });
                    FJMessageStore.addMessage(FJLocalStore._('failed-to-check-youtube-link'), 'error');
                    __endProgress();
                    dispatch(FJModalsAction.hideLoadingModalAction());
                }
            }
        },
};

export default FJYoutubeAction;
